/**
 * Session recording: capture, on-device safekeeping and chunked upload.
 *
 * Failure modes this exists to kill:
 *  - iOS Safari/Chrome can't record `video/webm` — it needs mp4, and recording
 *    a canvas-derived track can fail outright, so we fall back: processed
 *    stream → raw camera stream → audio only (audio is all the transcript
 *    needs).
 *  - A single multi-hundred-MB POST can die on a proxy body limit or a flaky
 *    phone connection, and the old code uploaded fire-and-forget AFTER
 *    navigating away, so failures were invisible. Uploads are now chunked,
 *    reported, retried, and the blob is parked in IndexedDB until the server
 *    confirms it — so a failed session can still be recovered later.
 */
import api from "./api";

const VIDEO_MIMES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4",
];
const AUDIO_MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];

const supported = (list) => {
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  for (const m of list) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch { /* Safari throws on odd strings */ }
  }
  return "";
};

export const extForMime = (mime = "") => {
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("mpeg")) return "mp3";
  if (mime.startsWith("audio/webm")) return "weba";
  return "webm";
};

/**
 * Starts recording, trying progressively safer sources. Returns a handle with
 * `stop()` resolving to the finished blob, or null when the browser can't
 * record at all.
 */
export function startSessionRecording({ processedStream, rawStream }) {
  const attempts = [];
  const videoMime = supported(VIDEO_MIMES);
  const audioMime = supported(AUDIO_MIMES);

  if (processedStream) attempts.push({ stream: processedStream, mime: videoMime, kind: "video" });
  if (rawStream && rawStream !== processedStream) attempts.push({ stream: rawStream, mime: videoMime, kind: "video" });
  const audioSource = rawStream || processedStream;
  const audioTracks = audioSource ? audioSource.getAudioTracks() : [];
  if (audioTracks.length) {
    attempts.push({ stream: new MediaStream(audioTracks), mime: audioMime, kind: "audio" });
  }

  for (const attempt of attempts) {
    if (!attempt.stream || attempt.stream.getTracks().length === 0) continue;
    try {
      const options = { };
      if (attempt.mime) options.mimeType = attempt.mime;
      if (attempt.kind === "video") options.videoBitsPerSecond = 900000;
      options.audioBitsPerSecond = 96000;
      const recorder = new MediaRecorder(attempt.stream, options);
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      recorder.onerror = (e) => console.error("MediaRecorder error", e);
      recorder.start(1000);
      const mime = recorder.mimeType || attempt.mime || (attempt.kind === "audio" ? "audio/webm" : "video/webm");
      return {
        kind: attempt.kind,
        mime,
        ext: extForMime(mime),
        get state() { return recorder.state; },
        pause() { if (recorder.state === "recording") recorder.pause(); },
        resume() { if (recorder.state === "paused") recorder.resume(); },
        stop() {
          return new Promise((resolve) => {
            if (recorder.state === "inactive") {
              resolve(chunks.length ? new Blob(chunks, { type: mime }) : null);
              return;
            }
            recorder.onstop = () => resolve(chunks.length ? new Blob(chunks, { type: mime }) : null);
            try {
              recorder.stop();
            } catch (e) {
              console.error("recorder.stop failed", e);
              resolve(chunks.length ? new Blob(chunks, { type: mime }) : null);
            }
          });
        },
      };
    } catch (e) {
      console.error(`Recording attempt failed (${attempt.kind}/${attempt.mime || "default"})`, e);
    }
  }
  return null;
}

// -------------------- Chunked upload --------------------
const CHUNK_SIZE = 4 * 1024 * 1024;

/**
 * Uploads a recording. Small blobs go in one request; anything bigger is split
 * so no single request can trip a proxy body limit. `onProgress` gets 0-1.
 */
export async function uploadRecording(meetingId, blob, { mime, ext, onProgress } = {}) {
  const type = mime || blob.type || "video/webm";
  const extension = ext || extForMime(type);
  const report = (v) => { if (onProgress) onProgress(Math.max(0, Math.min(1, v))); };

  if (blob.size <= CHUNK_SIZE) {
    const form = new FormData();
    form.append("file", new File([blob], `${meetingId}.${extension}`, { type }));
    const r = await api.post(`/meetings/${meetingId}/upload`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => { if (e.total) report(e.loaded / e.total); },
    });
    report(1);
    return r.data;
  }

  // Stable per-session id so a retry resumes instead of starting over.
  const uploadId = `sess-${meetingId}`.replace(/[^a-zA-Z0-9-_]/g, "").slice(0, 64);
  const total = Math.ceil(blob.size / CHUNK_SIZE);
  let done = new Set();
  try {
    const status = await api.get(`/meetings/${meetingId}/upload-chunk-status`, { params: { upload_id: uploadId } });
    done = new Set(status.data?.received || []);
  } catch (e) {
    console.debug("Could not read upload status — starting from the first chunk", e);
  }

  let last = null;
  for (let i = 0; i < total; i += 1) {
    if (done.has(i)) { report((i + 1) / total); continue; }
    const slice = blob.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const form = new FormData();
    form.append("upload_id", uploadId);
    form.append("index", String(i));
    form.append("total", String(total));
    form.append("ext", extension);
    form.append("mime", type);
    form.append("file", new File([slice], `${meetingId}.part${i}`, { type: "application/octet-stream" }));
    // One retry per chunk — phone networks blip.
    let attempt = 0;
    for (;;) {
      try {
        const r = await api.post(`/meetings/${meetingId}/upload-chunk`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        last = r.data;
        break;
      } catch (e) {
        attempt += 1;
        if (attempt > 1) throw e;
        await new Promise((res) => setTimeout(res, 1500));
      }
    }
    report((i + 1) / total);
  }
  return last;
}

// -------------------- Pending blob safekeeping (IndexedDB) --------------------
const DB_NAME = "boardroom_recordings";
const STORE = "pending";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no indexedDB")); return; }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "meetingId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const tx = async (mode, run) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = run(t.objectStore(STORE));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
  });
};

export async function savePendingRecording(meetingId, blob, mime, ext) {
  try {
    await tx("readwrite", (store) => store.put({ meetingId, blob, mime, ext, savedAt: Date.now() }));
  } catch (e) {
    console.debug("Could not park the recording on this device", e);
  }
}

export async function clearPendingRecording(meetingId) {
  try {
    await tx("readwrite", (store) => store.delete(meetingId));
  } catch (e) {
    console.debug("Could not clear the parked recording", e);
  }
}

export async function listPendingRecordings() {
  try {
    return (await tx("readonly", (store) => store.getAll())) || [];
  } catch {
    return [];
  }
}
